/**
 * Copyright © 2026 Rémi Pace.
 * This file is part of Abc-Map.
 *
 * Abc-Map is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of
 * the License, or (at your option) any later version.
 *
 * Abc-Map is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General
 * Public License along with Abc-Map. If not, see <https://www.gnu.org/licenses/>.
 */

import type { MapWrapper } from './MapWrapper';
import type { E2eMap } from '@abc-map/shared';
import { Logger, DEFAULT_PROJECTION } from '@abc-map/shared';
import type { LayerMetadata, BaseMetadata } from '@abc-map/shared';
import type Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import View from 'ol/View';
import { fromLonLat } from 'ol/proj';

export const logger = Logger.get('E2eMapWrapper.ts', 'debug');

/**
 * This class wrap managed maps to offer an interface for E2E tests.
 *
 * OK this is a bit hacky, but:
 * - it worth it, in order to get tests for complex scenarios like drawing
 * - it is type safe
 * - it is cheap
 */
export class E2eMapWrapper implements E2eMap {
  constructor(private readonly internal: MapWrapper) {}

  public getLayersMetadata(): LayerMetadata[] {
    return this.internal
      .getLayers()
      .map((lay) => lay.getMetadata())
      .filter((meta): meta is LayerMetadata => !!meta);
  }

  public getActiveLayerMetadata(): BaseMetadata | undefined {
    const layer = this.internal.getActiveLayer();
    if (!layer) {
      return;
    }

    return layer.getMetadata();
  }

  public getActiveLayerFeatures(): Feature<Geometry>[] {
    const layer = this.internal.getActiveVectorLayer();
    if (!layer) {
      return [];
    }

    return layer.getSource().getFeatures();
  }

  public setView(zoom: number, lon: number, lat: number): void {
    const view = new View({
      center: fromLonLat([lon, lat], DEFAULT_PROJECTION.name),
      zoom,
      projection: DEFAULT_PROJECTION.name,
    });

    this.internal.unwrap().setView(view);
  }

  public getViewExtent() {
    return this.internal.unwrap().getView().calculateExtent();
  }

  public unwrap(): MapWrapper {
    return this.internal;
  }
}
